/**
 * FishSpeciesCard
 * Displays a single fish species in the Fishipedia.
 * Shows full details for discovered fish, mystery card for undiscovered.
 */

import { FishDiscovery, QualityColors, QualityNames, FishRarity, QUALITY_ORDER } from '@/types';
import type { FishDefinition } from '@/types';
import { formatWeight, formatLength } from '@/utils';
import { formatCurrency } from '@/utils';
import { ZONE_DEFINITIONS } from '@/data';

const rarityColors: Record<FishRarity, string> = {
  [FishRarity.COMMON]: 'text-gray-300',
  [FishRarity.UNCOMMON]: 'text-green-400',
  [FishRarity.RARE]: 'text-blue-400',
  [FishRarity.EPIC]: 'text-purple-400',
  [FishRarity.LEGENDARY]: 'text-orange-400',
};

const rarityBgColors: Record<FishRarity, string> = {
  [FishRarity.COMMON]: 'bg-gray-600/30 border-gray-600',
  [FishRarity.UNCOMMON]: 'bg-green-900/30 border-green-700/50',
  [FishRarity.RARE]: 'bg-blue-900/30 border-blue-700/50',
  [FishRarity.EPIC]: 'bg-purple-900/30 border-purple-700/50',
  [FishRarity.LEGENDARY]: 'bg-orange-900/30 border-orange-700/50',
};

const rarityNames: Record<FishRarity, string> = {
  [FishRarity.COMMON]: 'Common',
  [FishRarity.UNCOMMON]: 'Uncommon',
  [FishRarity.RARE]: 'Rare',
  [FishRarity.EPIC]: 'Epic',
  [FishRarity.LEGENDARY]: 'Legendary',
};

interface FishSpeciesCardProps {
  definition: FishDefinition;
  discovery: FishDiscovery | null;
}

/** Find which zone(s) contain this fish */
function getZoneHints(fishId: string): string[] {
  return ZONE_DEFINITIONS
    .filter((z) => z.fishPool.includes(fishId))
    .map((z) => z.name);
}

export function FishSpeciesCard({ definition, discovery }: FishSpeciesCardProps) {
  const isDiscovered = discovery !== null;

  if (!isDiscovered) {
    return <UndiscoveredCard definition={definition} />;
  }

  return <DiscoveredCard definition={definition} discovery={discovery} />;
}

function UndiscoveredCard({ definition }: { definition: FishDefinition }) {
  const zones = getZoneHints(definition.id);

  return (
    <div className="bg-gray-800/40 rounded-lg p-4 border border-gray-700/50 opacity-70">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <span className="text-3xl grayscale opacity-50">?</span>
        <div className="flex-1">
          <div className="font-bold text-gray-500">???</div>
          <span className={`text-xs font-semibold ${rarityColors[definition.rarity]}`}>
            {rarityNames[definition.rarity]}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="text-sm text-gray-500 mt-2">Not yet caught</div>
      {zones.length > 0 && (
        <div className="text-xs text-gray-600 mt-1">
          Hint: Found in {zones.join(', ')}
        </div>
      )}
    </div>
  );
}

function DiscoveredCard({ definition, discovery }: { definition: FishDefinition; discovery: FishDiscovery }) {
  const sortedQualities = [...discovery.qualitiesSeen].sort(
    (a, b) => QUALITY_ORDER[a] - QUALITY_ORDER[b],
  );

  const firstCaughtDate = new Date(discovery.discoveredAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className={`rounded-lg p-4 border ${rarityBgColors[definition.rarity]}`}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="text-3xl">{definition.emoji || definition.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-white truncate">{definition.name}</div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold ${rarityColors[definition.rarity]}`}>
              {rarityNames[definition.rarity]}
            </span>
            <span className="text-xs text-gray-400">
              {formatCurrency(definition.baseValue)}g base
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      {definition.description && (
        <p className="text-xs text-gray-400 mt-2 italic">{definition.description}</p>
      )}

      {/* Records */}
      {discovery.records && (discovery.records.maxLength > 0 || discovery.records.maxWeight > 0) && (
        <div className="mt-3 bg-gray-800/50 rounded p-2.5 border border-gray-700/50">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 font-semibold">
            Personal Records
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div>
              <div className="text-gray-400">Length</div>
              <div className="text-white font-semibold">{formatLength(discovery.records.maxLength)}</div>
            </div>
            <div>
              <div className="text-gray-400">Weight</div>
              <div className="text-white font-semibold">{formatWeight(discovery.records.maxWeight)}</div>
            </div>
            <div>
              <div className="text-gray-400">Best</div>
              <div
                className="font-semibold"
                style={{ color: QualityColors[discovery.records.bestQuality] }}
              >
                {QualityNames[discovery.records.bestQuality]}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Qualities Seen */}
      {sortedQualities.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 font-semibold">
            Qualities Seen
          </div>
          <div className="flex flex-wrap gap-1.5">
            {sortedQualities.map((quality) => (
              <span
                key={quality}
                className="text-[11px] font-semibold px-2 py-0.5 rounded-full border"
                style={{
                  color: QualityColors[quality],
                  borderColor: QualityColors[quality] + '40',
                  backgroundColor: QualityColors[quality] + '15',
                }}
              >
                {QualityNames[quality]}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* First Caught */}
      <div className="mt-3 text-[11px] text-gray-500">
        First caught: {firstCaughtDate}
      </div>
    </div>
  );
}
